"use client";

/**
 * ScrollResetOnMount — workaround for Next.js App Router not scrolling to top
 * on push navigation when our artifact pages mount (BrandPresentationV2,
 * PlanPresentationV2, NicheDetailV2). Their nested `<main>` shape causes
 * Next.js' focus-driven scroll-to-top to land in <body> and skip the reset,
 * so the new page inherits the previous page's scrollY (e.g. clicking a
 * brand link from a scrolled dashboard opens the brand page mid-scroll).
 *
 * Behavior:
 *   - On mount, scroll to (0, 0).
 *   - Skip if URL has a hash → caller wants an anchor jump.
 *   - Skip if the most recent navigation was popstate (back/forward) → the
 *     browser has already restored the saved scroll position; clobbering it
 *     would break expected back-nav UX.
 *
 * Detection: explicit boolean flag set on `popstate`, cleared on next
 * `pushState` (forward navigation). `replaceState` does NOT clear the flag —
 * Next.js uses `replaceState` internally for scroll-restoration bookkeeping
 * and clearing it would re-introduce the bug. Replaces a brittle 250ms
 * timing heuristic (S111 UI-RT-02): on slow devices (LTE / older Android)
 * the component could mount >250ms after popstate fired, causing a false
 * miss → unwanted scroll reset on browser back. The flag pattern is
 * race-free.
 *
 * Usage: drop one instance inside the artifact root client component.
 */
import { useEffect } from "react";

let isPopstateNavigation = false;

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    isPopstateNavigation = true;
  });

  // Patch pushState to clear the flag — normal forward navigation should
  // reset scroll, only browser back/forward should preserve it.
  // replaceState is intentionally NOT patched (Next.js scroll-restoration).
  const originalPushState = window.history.pushState.bind(window.history);
  window.history.pushState = function (
    ...args: Parameters<typeof originalPushState>
  ) {
    isPopstateNavigation = false;
    return originalPushState(...args);
  };
}

export function ScrollResetOnMount() {
  useEffect(() => {
    if (window.location.hash) return;
    if (isPopstateNavigation) {
      isPopstateNavigation = false; // consume — clear after first mount
      return;
    }
    if (window.scrollY === 0) return;
    window.scrollTo(0, 0);
  }, []);
  return null;
}
